import Phaser from 'phaser';
import { GAME_W, GAME_H, GAME_CX, GAME_CY } from '../utils/MobileHelper';
import { FONT_BODY } from '../utils/Typography';

// === SYSTÈME DE TUTORIEL ÉTAPE PAR ÉTAPE ===
// Affiche des bulles de dialogue qui guident le joueur dans ses premières actions.
// Chaque étape pointe vers un élément de l'écran avec un texte explicatif.

const TUTO_KEY = 'deadtide_tutorial_done';

export interface TutorialStep {
  text: string;
  /** Position de la bulle (centre) */
  x: number;
  y: number;
  /** Flèche vers un élément (optionnel) */
  arrowTo?: { x: number; y: number };
}

export class TutorialOverlay {
  private scene: Phaser.Scene;
  private steps: TutorialStep[];
  private currentStep = 0;
  private overlay!: Phaser.GameObjects.Rectangle;
  private bubble!: Phaser.GameObjects.Rectangle;
  private text!: Phaser.GameObjects.Text;
  private counter!: Phaser.GameObjects.Text;
  private nextBtn!: Phaser.GameObjects.Text;
  private arrow: Phaser.GameObjects.Graphics | null = null;
  private onComplete: () => void;

  constructor(scene: Phaser.Scene, steps: TutorialStep[], onComplete: () => void) {
    this.scene = scene;
    this.steps = steps;
    this.onComplete = onComplete;
  }

  /** Vérifie si le tuto a déjà été fait */
  static isDone(): boolean {
    try {
      return localStorage.getItem(TUTO_KEY) === '1';
    } catch {
      return false;
    }
  }

  /** Marque le tuto comme terminé */
  static markDone(): void {
    try {
      localStorage.setItem(TUTO_KEY, '1');
    } catch { /* */ }
  }

  /** Reset le tuto (debug) */
  static reset(): void {
    try {
      localStorage.removeItem(TUTO_KEY);
    } catch { /* */ }
  }

  /** Lance le tutoriel */
  start(): void {
    // Fond semi-transparent
    this.overlay = this.scene.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x000000, 0.6)
      .setDepth(200).setInteractive();

    // Bulle — largeur clampée pour rester dans le canvas (mobile 450 / desktop 800)
    const initialBubbleW = Math.min(500, GAME_W - 30);
    this.bubble = this.scene.add.rectangle(GAME_CX, 350, initialBubbleW, 120, 0x0d1a2e, 0.95)
      .setStrokeStyle(2, 0xf1c40f).setDepth(201);

    // Texte
    this.text = this.scene.add.text(GAME_CX, 330, '', {
      fontSize: '12px', color: '#ecf0f1', fontFamily: FONT_BODY,
      align: 'center', lineSpacing: 6, wordWrap: { width: Math.min(460, GAME_W - 50) },
    }).setOrigin(0.5).setDepth(202);

    // Compteur d'étapes
    this.counter = this.scene.add.text(GAME_CX, 390, '', {
      fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5).setDepth(202);

    // Bouton suivant
    this.nextBtn = this.scene.add.text(GAME_CX, 415, '[ Suivant → ]', {
      fontSize: '13px', color: '#2ecc71', fontFamily: FONT_BODY,
      backgroundColor: '#1a2a1a', padding: { x: 14, y: 6 },
    }).setOrigin(0.5).setDepth(202);
    this.nextBtn.setInteractive({ useHandCursor: true });
    this.nextBtn.on('pointerdown', () => this.next());
    this.nextBtn.on('pointerover', () => this.nextBtn.setColor('#27ae60'));
    this.nextBtn.on('pointerout', () => this.nextBtn.setColor('#2ecc71'));

    this.showStep();
  }

  private showStep(): void {
    const step = this.steps[this.currentStep];
    const isLast = this.currentStep === this.steps.length - 1;

    // D'abord set le texte pour mesurer sa hauteur
    this.text.setText(step.text);
    const textH = this.text.height;
    const padding = 30;
    const bottomSpace = 50; // compteur + bouton
    const bubbleH = textH + padding * 2 + bottomSpace;
    // Clamp à la largeur du canvas — sur desktop GAME_W=800 donc on garde la valeur cible
    const bubbleW = Math.min(GAME_W - 30, Math.max(420, this.text.width + 60));

    // Positionner la bulle centrée sur le texte
    const bubbleY = step.y;
    const textY = bubbleY - bottomSpace / 2;

    this.bubble.setPosition(step.x, bubbleY);
    this.bubble.setSize(bubbleW, bubbleH);
    this.text.setPosition(step.x, textY);
    this.counter.setPosition(step.x, bubbleY + bubbleH / 2 - 35);
    this.counter.setText(`${this.currentStep + 1}/${this.steps.length}`);
    this.nextBtn.setPosition(step.x, bubbleY + bubbleH / 2 - 14);
    this.nextBtn.setText(isLast ? '[ C\'est parti ! ]' : '[ Suivant → ]');

    // Flèche vers l'élément (LOT C1 fix : Graphics au lieu de Triangle car
    // Triangle de Phaser applique un offset de bounding-box qui décalait
    // la pointe par rapport à arrowTo. Graphics utilise des coords absolues.)
    if (this.arrow) { this.arrow.destroy(); this.arrow = null; }
    if (step.arrowTo) {
      const dx = step.arrowTo.x - step.x;
      const dy = step.arrowTo.y - step.y;
      const len = Math.sqrt(dx * dx + dy * dy) || 1;
      const nx = dx / len;
      const ny = dy / len;
      const tipX = step.arrowTo.x;
      const tipY = step.arrowTo.y;
      const baseX = tipX - nx * 22;
      const baseY = tipY - ny * 22;
      const g = this.scene.add.graphics().setDepth(201);
      g.fillStyle(0xf1c40f, 1);
      g.beginPath();
      g.moveTo(tipX, tipY);                       // pointe
      g.lineTo(baseX - ny * 9, baseY + nx * 9);   // base gauche
      g.lineTo(baseX + ny * 9, baseY - nx * 9);   // base droite
      g.closePath();
      g.fillPath();
      // Petit halo pulsant pour attirer l'œil
      g.lineStyle(2, 0xfff3a0, 0.6);
      g.beginPath();
      g.moveTo(tipX, tipY);
      g.lineTo(baseX - ny * 9, baseY + nx * 9);
      g.lineTo(baseX + ny * 9, baseY - nx * 9);
      g.closePath();
      g.strokePath();
      this.arrow = g;
    }
  }

  private next(): void {
    this.currentStep++;
    if (this.currentStep >= this.steps.length) {
      this.destroy();
      TutorialOverlay.markDone();
      this.onComplete();
      return;
    }
    this.showStep();
  }

  private destroy(): void {
    this.overlay.destroy();
    this.bubble.destroy();
    this.text.destroy();
    this.counter.destroy();
    this.nextBtn.destroy();
    if (this.arrow) this.arrow.destroy();
  }
}
